import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { ReferralFormFields, ReferralFormInputs, ReferralsQueryKeys, useCreateReferralsMutation } from '@/modules/Sites';

import { ReferralsFormView } from './ReferralsFormView';
import { referralFormValidationSchema } from './validation';

interface Props {
  siteId: string;
}

export const ReferralsCreateFormContainer: FC<Props> = memo(({ siteId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<ReferralFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof ReferralFormInputs>>(null);

  const { mutate: createReferralMutation, isLoading: createReferralsIsLoading } =
  useCreateReferralsMutation(siteId)({
    onSuccess: async (data) => {
      await queryClient.refetchQueries([ReferralsQueryKeys.GetAll]);

      appMessageApi.success('Реферал успішно створено!');

      navigate(AppRoutes.ReferralsById.replace(':siteId', siteId).replace(':referralId', data.id));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof ReferralFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: ReferralFormFields = {
    name: undefined,
    siteId,
    slug: undefined,
  };

  const handleFinish = useCallback((values: ReferralFormFields): void => {
    setFormErrors(null);
    createReferralMutation(values);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.Referrals.replace(':siteId', siteId));
  }, [siteId]);

  return (
    <ReferralsFormView
      formName="referral-create"
      formType="create"
      form={ form }
      formErrors={ formErrors }
      initialValues={ initialValues }
      isFormLoading={ createReferralsIsLoading }
      validationSchema={ referralFormValidationSchema }
      handleBack={ handleBack }
      handleFinish={ handleFinish }
      handleFinishFailed={ handleFinishFailed }
    />
  );
});
